import { useCallback, useEffect, useRef, useState } from "react";
import {
  addConnectedAccount,
  fetchConnectedAccounts,
  MAX_ACCOUNTS_PER_NETWORK,
  NETWORK_META,
  parseAccountInput,
  removeConnectedAccount,
  scrapeFromAccount,
  searchProfiles,
  setConnectedAccountActive,
  type AccountNetwork,
  type ConnectedAccount,
  type ProfileCandidate,
} from "@/services/accounts";

const NETWORKS: AccountNetwork[] = ["instagram", "facebook", "tiktok"];

const APP_LINK: Record<AccountNetwork, string> = {
  instagram: "https://www.instagram.com/",
  facebook: "https://www.facebook.com/me",
  tiktok: "https://www.tiktok.com/",
};

/** Red a la que pertenece un link (null si no es de ninguna). */
function networkOfUrl(text: string): AccountNetwork | null {
  const m = text.match(/https?:\/\/[^\s]+/i);
  if (!m) return null;
  try {
    const host = new URL(m[0]).hostname.replace(/^(www|m)\./, "");
    if (host === "instagram.com") return "instagram";
    if (host === "facebook.com" || host === "fb.com") return "facebook";
    if (host === "tiktok.com") return "tiktok";
  } catch {
    // link inválido
  }
  return null;
}

/** Solo acepta del portapapeles un link de esa red o un @usuario, para no llenar el campo con texto cualquiera. */
function fromClipboard(n: AccountNetwork, raw: string): string | null {
  const text = raw.trim();
  if (!text || text.length > 200) return null;
  const url = text.match(/https?:\/\/[^\s]+/i)?.[0];
  if (url) return networkOfUrl(url) === n && parseAccountInput(n, url) ? url : null;
  return /^@[A-Za-z0-9._-]{2,60}$/.test(text) ? text : null;
}

function ago(iso: string | null): string {
  if (!iso) return "Aún no se ha buscado";
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `Hace ${m} min`;
  const h = Math.round(m / 60);
  return h < 24 ? `Hace ${h} h` : `Hace ${Math.round(h / 24)} d`;
}

export default function ConnectedAccounts({ onProspects, notify: notifyProp }: { onProspects: () => void; notify: (msg: string) => void }) {
  const notifyRef = useRef(notifyProp);
  notifyRef.current = notifyProp;
  const notify = useCallback((m: string) => notifyRef.current(m), []);
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<AccountNetwork, string>>({ instagram: "", facebook: "", tiktok: "" });
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Partial<Record<AccountNetwork, ProfileCandidate[]>>>({});
  const [hint, setHint] = useState<Partial<Record<AccountNetwork, string>>>({});

  const load = useCallback(async () => {
    try {
      setAccounts(await fetchConnectedAccounts());
    } catch {
      notify("No pudimos cargar tus cuentas conectadas.");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  // Llegada desde "Compartir perfil → Vívelo AI" (app instalada): rellena el campo de la red que corresponda.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const shared = [q.get("url"), q.get("text"), q.get("title")].filter(Boolean).join(" ");
    if (!shared) return;
    const net = networkOfUrl(shared);
    const link = shared.match(/https?:\/\/[^\s]+/i)?.[0];
    window.history.replaceState(null, "", window.location.pathname);
    if (!net || !link) return;
    if (!parseAccountInput(net, link)) {
      notifyRef.current("Ese link no es de un perfil. Comparte el perfil, no una publicación.");
      return;
    }
    setOpen(true);
    setDrafts((d) => ({ ...d, [net]: link }));
    setHint((h) => ({ ...h, [net]: "Lo recibimos desde tu app. Toca Conectar para confirmar." }));
  }, []);

  /** Al tocar el campo: toma el perfil que copiaste en la app o sugiere el mismo usuario de otra red. */
  const onFocusField = async (n: AccountNetwork) => {
    if (drafts[n].trim()) return;
    try {
      const clip = await navigator.clipboard.readText();
      const found = fromClipboard(n, clip);
      if (found) {
        setDrafts((d) => ({ ...d, [n]: found }));
        setHint((h) => ({ ...h, [n]: "Rellenado con el perfil que copiaste." }));
        return;
      }
    } catch {
      // sin permiso de portapapeles: se queda la sugerencia manual
    }
    const other = accounts.find((a) => a.network !== n);
    setHint((h) => ({ ...h, [n]: other ? `¿Usas el mismo usuario? @${other.handle}` : "" }));
  };

  /** Link de la red o @usuario: se conecta directo; cualquier otro texto se busca por nombre. */
  const isDirect = (n: AccountNetwork, text: string) => {
    const t = text.trim();
    return t.startsWith("@") || networkOfUrl(t) === n;
  };

  const search = async (n: AccountNetwork) => {
    setBusy(`search-${n}`);
    setResults((r) => ({ ...r, [n]: undefined }));
    try {
      const list = await searchProfiles(n, drafts[n].trim());
      setResults((r) => ({ ...r, [n]: list }));
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo buscar.");
    } finally {
      setBusy(null);
    }
  };

  const link = async (n: AccountNetwork, c: ProfileCandidate) => {
    setBusy(`add-${n}`);
    try {
      await addConnectedAccount(n, c.url);
      setDrafts((d) => ({ ...d, [n]: "" }));
      setResults((r) => ({ ...r, [n]: undefined }));
      await load();
      notify(`@${c.handle} vinculada a ${NETWORK_META[n].label}`);
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo vincular la cuenta.");
    } finally {
      setBusy(null);
    }
  };

  const submit = (n: AccountNetwork) => (isDirect(n, drafts[n]) ? add(n) : search(n));

  const add = async (n: AccountNetwork) => {
    setBusy(`add-${n}`);
    try {
      await addConnectedAccount(n, drafts[n]);
      setDrafts((d) => ({ ...d, [n]: "" }));
      await load();
      notify(`Cuenta de ${NETWORK_META[n].label} conectada`);
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo conectar la cuenta.");
    } finally {
      setBusy(null);
    }
  };

  const scrape = async (a: ConnectedAccount) => {
    setBusy(`run-${a.id}`);
    try {
      const r = await scrapeFromAccount(a.id);
      await load();
      if (r.inserted > 0) {
        notify(`${r.inserted} prospecto${r.inserted === 1 ? "" : "s"} nuevo${r.inserted === 1 ? "" : "s"} desde @${a.handle}${r.spam ? ` · ${r.spam} spam descartado${r.spam === 1 ? "" : "s"}` : ""}`);
        onProspects();
      } else if (r.message === "no_posts") notify("No encontramos publicaciones públicas en esa cuenta. Revisa que sea pública.");
      else if (r.message === "only_spam") notify("Solo encontramos comentarios que parecen spam; no los guardamos.");
      else if (r.message === "no_comments") notify("Tus publicaciones recientes aún no tienen comentarios de otras personas.");
      else notify("Esas personas ya estaban en tu lista.");
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo completar la búsqueda.");
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (a: ConnectedAccount) => {
    try {
      await setConnectedAccountActive(a.id, !a.active);
      await load();
    } catch {
      notify("No se pudo actualizar la cuenta.");
    }
  };

  const remove = async (a: ConnectedAccount) => {
    try {
      await removeConnectedAccount(a.id);
      await load();
    } catch {
      notify("No se pudo quitar la cuenta.");
    }
  };

  const total = accounts.length;

  return (
    <section className="rounded-2xl bg-background-100 border border-background-200 mb-5">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center gap-3 p-4 text-left cursor-pointer">
        <span className="w-10 h-10 rounded-xl bg-primary-500/15 text-primary-600 flex items-center justify-center flex-shrink-0">
          <i className="ri-links-line text-xl"></i>
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-bold text-foreground-950">Mis cuentas conectadas</span>
          <span className="block text-xs text-foreground-500">
            {total === 0 ? "Conecta hasta 3 cuentas por red y la IA busca prospectos entre quienes interactúan con tu contenido." : `${total} cuenta${total === 1 ? "" : "s"} conectada${total === 1 ? "" : "s"}`}
          </span>
        </span>
        <i className={`ri-arrow-down-s-line text-xl text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`}></i>
      </button>

      {open ? (
        <div className="px-4 pb-4 grid gap-4 md:grid-cols-3">
          {NETWORKS.map((n) => {
            const list = accounts.filter((a) => a.network === n);
            const full = list.length >= MAX_ACCOUNTS_PER_NETWORK;
            return (
              <div key={n} className="rounded-xl bg-background-50 border border-background-200 p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground-950 flex items-center gap-1.5">
                    <i className={NETWORK_META[n].icon}></i>
                    {NETWORK_META[n].label}
                  </span>
                  <span className="text-[11px] text-foreground-500">
                    {list.length}/{MAX_ACCOUNTS_PER_NETWORK}
                  </span>
                </div>

                {loading ? <div className="h-10 rounded-lg bg-background-200 animate-pulse"></div> : null}

                {list.map((a) => (
                  <div key={a.id} className={`rounded-lg border border-background-200 bg-background-100 p-2.5 ${a.active ? "" : "opacity-60"}`}>
                    <div className="flex items-center gap-2">
                      <a href={a.profileUrl} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground-950 hover:underline">
                        @{a.handle}
                      </a>
                      <button onClick={() => toggle(a)} className="text-[11px] font-semibold text-foreground-600 cursor-pointer whitespace-nowrap" aria-label={a.active ? "Pausar cuenta" : "Activar cuenta"}>
                        {a.active ? "Activa" : "Pausada"}
                      </button>
                      <button onClick={() => remove(a)} className="text-foreground-400 hover:text-red-500 cursor-pointer" aria-label={`Quitar @${a.handle}`}>
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                    <div className="text-[11px] text-foreground-500 mt-0.5">
                      {ago(a.lastScrapedAt)}
                      {a.lastResult.inserted != null ? ` · ${a.lastResult.inserted} nuevos` : ""}
                    </div>
                    <button
                      onClick={() => scrape(a)}
                      disabled={!a.active || busy !== null}
                      className="mt-2 w-full px-3 py-1.5 rounded-lg bg-primary-500 text-background-950 text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {busy === `run-${a.id}` ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-1"></i>Buscando…
                        </>
                      ) : (
                        <>
                          <i className="ri-sparkling-2-fill mr-1"></i>Buscar prospectos
                        </>
                      )}
                    </button>
                  </div>
                ))}

                {!full ? (
                  <div className="flex gap-1.5">
                    <input
                      value={drafts[n]}
                      onFocus={() => void onFocusField(n)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDrafts((d) => ({ ...d, [n]: v }));
                        if (v) setHint((h) => ({ ...h, [n]: "" }));
                      }}
                      onKeyDown={(e) => e.key === "Enter" && drafts[n].trim() && submit(n)}
                      placeholder="Tu nombre en la red, @usuario o link"
                      maxLength={120}
                      className="flex-1 min-w-0 rounded-lg border border-background-200 bg-background-100 px-2.5 py-1.5 text-xs text-foreground-950 outline-none focus:border-primary-500"
                      aria-label={`Cuenta de ${NETWORK_META[n].label}`}
                    />
                    <button onClick={() => submit(n)} disabled={!drafts[n].trim() || busy !== null} className="px-3 py-1.5 rounded-lg border border-primary-500 text-primary-600 text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
                      {busy === `search-${n}` ? <i className="ri-loader-4-line animate-spin"></i> : isDirect(n, drafts[n]) ? "Conectar" : "Buscar"}
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-foreground-500">Llegaste al máximo de {MAX_ACCOUNTS_PER_NETWORK} cuentas.</p>
                )}

                {!full ? (
                  <div className="flex flex-col gap-1">
                    {hint[n] ? (
                      /^¿Usas/.test(hint[n]!) ? (
                        <button
                          type="button"
                          onClick={() => {
                            const other = accounts.find((a) => a.network !== n);
                            if (other) setDrafts((d) => ({ ...d, [n]: `@${other.handle}` }));
                            setHint((h) => ({ ...h, [n]: "" }));
                          }}
                          className="self-start px-2 py-1 rounded-full bg-primary-500/15 text-primary-700 text-[11px] font-semibold cursor-pointer"
                        >
                          {hint[n]} · usar
                        </button>
                      ) : (
                        <p className="text-[11px] text-primary-700 font-semibold">{hint[n]}</p>
                      )
                    ) : null}
                    {busy === `search-${n}` ? <p className="text-[11px] text-foreground-500">Buscando perfiles… puede tardar unos segundos.</p> : null}
                    {results[n] ? (
                      results[n]!.length === 0 ? (
                        <p className="text-[11px] text-foreground-500">No encontramos perfiles con ese nombre. Prueba con tu @usuario o pega el link.</p>
                      ) : (
                        <ul className="rounded-lg border border-background-200 divide-y divide-background-200 bg-background-100">
                          {results[n]!.map((c) => (
                            <li key={c.handle} className="p-2 flex items-center gap-2">
                              {c.photo ? (
                                <img src={c.photo} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover flex-shrink-0 bg-background-200" />
                              ) : (
                                <span className="w-8 h-8 rounded-full bg-background-200 flex-shrink-0"></span>
                              )}
                              <span className="flex-1 min-w-0">
                                <span className="block truncate text-xs font-semibold text-foreground-950">
                                  @{c.handle}
                                  {c.verified ? <i className="ri-verified-badge-fill text-sky-500 ml-1"></i> : null}
                                </span>
                                <span className="block truncate text-[11px] text-foreground-500">
                                  {c.name ?? ""}
                                  {c.followers != null ? ` · ${new Intl.NumberFormat("es").format(c.followers)} seguidores` : ""}
                                </span>
                              </span>
                              <button onClick={() => link(n, c)} disabled={busy !== null} className="px-2.5 py-1 rounded-lg bg-primary-500 text-background-950 text-[11px] font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
                                Vincular
                              </button>
                            </li>
                          ))}
                        </ul>
                      )
                    ) : null}
                    <a href={APP_LINK[n]} target="_blank" rel="noopener noreferrer" className="text-[11px] text-foreground-500 hover:text-foreground-800">
                      <i className="ri-smartphone-line mr-1"></i>Abrir {NETWORK_META[n].label} → copia el enlace de tu perfil → toca el campo
                    </a>
                  </div>
                ) : null}
              </div>
            );
          })}
          <p className="md:col-span-3 text-[11px] text-foreground-500">
            Solo cuentas públicas y propias. No pedimos contraseñas: la IA lee tus publicaciones recientes y guarda como prospectos a quienes comentaron en ellas, con el motivo. Cada cuenta se puede analizar cada 10 minutos.
          </p>
        </div>
      ) : null}
    </section>
  );
}
